import { useTranslation } from "react-i18next";
import { LogOut, User } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { LanguagePicker } from "@/components/i18n/LanguagePicker";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

interface SidebarFooterProps {
  collapsed: boolean;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/**
 * The bottom-anchored profile area: the avatar (with login/logout), the theme
 * selector immediately beside the profile name, and the language submenu.
 */
export function SidebarFooter({ collapsed }: SidebarFooterProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  if (!user) {
    return (
      <div className="border-t p-3">
        <Button asChild variant="outline" className={cn("w-full", collapsed && "px-2")}>
          <a href="/login">
            <User className="h-4 w-4" />
            {!collapsed ? <span>{t("auth.login")}</span> : null}
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className="border-t p-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted",
              collapsed && "justify-center px-0",
            )}
          >
            <Avatar>
              {user.avatar_url ? <AvatarImage src={user.avatar_url} alt={user.full_name} /> : null}
              <AvatarFallback>{initials(user.full_name)}</AvatarFallback>
            </Avatar>
            {!collapsed ? (
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{user.full_name}</span>
                <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
              </span>
            ) : null}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{user.full_name}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <a href="/profile">
              <User className="h-4 w-4" />
              {t("auth.profile")}
            </a>
          </DropdownMenuItem>
          <LanguagePicker />
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void logout()}>
            <LogOut className="h-4 w-4" />
            {t("auth.logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
